// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Assistant read-aloud in the Konvo voices (Kokoro on the server). A reply is
// split into short chunks; each chunk's audio is fetched while the previous one plays,
// so speech starts after the first sentence rather than the whole reply.
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';

export const KONVO_VOICES = [
	{id: 'konvo:af_heart', name: 'Heart'},
	{id: 'konvo:af_bella', name: 'Bella'},
	{id: 'konvo:am_liam', name: 'Liam'},
	{id: 'konvo:am_onyx', name: 'Onyx'},
] as const;
export const DEFAULT_KONVO_VOICE = KONVO_VOICES[0].id;
const FIRST_CHUNK_CHARS = 180;
// Voice mode: the first thing said is kept to a clause, since Kokoro takes about a
// third of the spoken length to render it and nothing plays until it has.
const FIRST_STREAM_CHARS = 60;
const MIN_CLAUSE_CHARS = 16;
const CHUNK_CHARS = 400;
const MAX_CHUNK_CHARS = 600;
const SILENCE =
	'data:audio/wav;base64,UklGRnQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YVAAAACAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgICAgA==';

export function isKonvoVoice(voiceId: string): boolean {
	return KONVO_VOICES.some((voice) => voice.id === voiceId);
}

function splitLongSentence(sentence: string): Array<string> {
	const parts: Array<string> = [];
	let rest = sentence;
	while (rest.length > MAX_CHUNK_CHARS) {
		const cut = rest.lastIndexOf(' ', MAX_CHUNK_CHARS);
		const at = cut > MAX_CHUNK_CHARS / 2 ? cut : MAX_CHUNK_CHARS;
		parts.push(rest.slice(0, at).trim());
		rest = rest.slice(at).trim();
	}
	if (rest) parts.push(rest);
	return parts;
}

function sentencesOf(text: string): Array<string> {
	return (
		text
			.replace(/\s+/g, ' ')
			.trim()
			.match(/[^.!?…]+(?:[.!?…]+["')\]]*|$)\s*/g) ?? []
	)
		.map((sentence) => sentence.trim())
		.filter(Boolean)
		.flatMap(splitLongSentence);
}

/** Splits a long opening sentence after its first clause (a comma, colon, semicolon or dash). */
export function splitFirstClause(sentence: string): Array<string> {
	if (sentence.length <= FIRST_STREAM_CHARS) return [sentence];
	const head = sentence.slice(0, FIRST_STREAM_CHARS);
	let cut = -1;
	for (const match of head.matchAll(/[,;:—–](?=\s)|\s-\s/g)) {
		if (match.index >= MIN_CLAUSE_CHARS) cut = match.index + match[0].trimEnd().length;
	}
	if (cut < 0) return [sentence];
	return [sentence.slice(0, cut).trim(), sentence.slice(cut).trim()].filter(Boolean);
}

/** How a streamed reply is cut into audio requests: sentence by sentence, the very first one cut after a clause. */
export function streamPieces(text: string, first: boolean): Array<string> {
	const pieces: Array<string> = [];
	for (const sentence of sentencesOf(text)) {
		pieces.push(...(first && pieces.length === 0 ? splitFirstClause(sentence) : [sentence]));
	}
	return pieces;
}

// Whole sentences grouped into chunks; the first chunk is kept short so playback starts quickly.
export function splitForSpeech(text: string): Array<string> {
	const sentences = sentencesOf(text);
	const chunks: Array<string> = [];
	let current = '';
	for (const sentence of sentences) {
		const limit = chunks.length === 0 ? FIRST_CHUNK_CHARS : CHUNK_CHARS;
		if (current && current.length + 1 + sentence.length > limit) {
			chunks.push(current);
			current = sentence;
		} else {
			current = current ? `${current} ${sentence}` : sentence;
		}
	}
	if (current) chunks.push(current);
	return chunks;
}

export interface KonvoSpeechStream {
	/** More of the reply; it plays after what is already queued. */
	push: (text: string) => void;
	/** Exactly these audio pieces, as cut (and already being rendered) by the server. */
	pushPieces: (pieces: Array<string>) => void;
	/** Nothing more is coming: onEnd fires once the queue has played. */
	end: () => void;
}

export interface KonvoSpeakOptions {
	voiceId: string;
	rate: number;
	onEnd: () => void;
	// Called with the text that was not read, so the caller can finish it another way.
	onError: (unread: string, unavailable: boolean) => void;
}

export class KonvoVoicePlayer {
	private audio: HTMLAudioElement | null = null;
	private generation = 0;
	private urls: Array<string> = [];
	private primed = false;

	/** `connect` lets a caller route the audio element through Web Audio (voice mode's orb listens to it). */
	constructor(private readonly connect?: (audio: HTMLAudioElement) => void) {}

	private element(): HTMLAudioElement {
		if (!this.audio) {
			this.audio = new Audio();
			this.connect?.(this.audio);
		}
		return this.audio;
	}

	// Mobile browsers only let audio start from a tap; playing a moment of silence during one unlocks
	// the element for later replies that arrive without a tap.
	prime(): void {
		if (this.primed) return;
		const audio = this.element();
		if (audio.src) return;
		this.primed = true;
		audio.src = SILENCE;
		void audio
			.play()
			.catch(() => undefined)
			.finally(() => {
				if (audio.src === SILENCE) audio.removeAttribute('src');
			});
	}

	speak(text: string, options: KonvoSpeakOptions): void {
		this.stop();
		const generation = this.generation;
		const chunks = splitForSpeech(text);
		const voice = options.voiceId.replace(/^konvo:/, '');
		const fetchChunk = (index: number) =>
			http
				.post<Blob>(Endpoints.KONVO_ASSISTANT_SPEECH, {
					body: {text: chunks[index], voice, speed: options.rate},
					parse: 'binary',
				})
				.then((response) => {
					if (!(response.body instanceof Blob)) throw new Error('No audio');
					return response.body;
				});
		const fail = (index: number, error: unknown) => {
			if (generation !== this.generation) return;
			this.stop();
			const unavailable = error instanceof HttpError && (error.status === 503 || error.status === 404);
			options.onError(chunks.slice(index).join(' '), unavailable);
		};
		const play = (index: number, pending: Promise<Blob>) => {
			if (generation !== this.generation) return;
			if (index >= chunks.length) {
				this.stop();
				options.onEnd();
				return;
			}
			const next = index + 1 < chunks.length ? fetchChunk(index + 1) : null;
			next?.catch(() => undefined);
			pending
				.then((blob) => {
					if (generation !== this.generation) return;
					const audio = this.element();
					const url = URL.createObjectURL(blob);
					this.urls.push(url);
					audio.onended = () => play(index + 1, next ?? Promise.resolve(new Blob()));
					audio.onerror = () => fail(index, new Error('Playback failed'));
					audio.src = url;
					return audio.play();
				})
				.catch((error) => fail(index, error));
		};
		if (chunks.length === 0) {
			options.onEnd();
			return;
		}
		play(0, fetchChunk(0));
	}

	/**
	 * Voice mode: speak a reply while it is still being written. Each pushed piece
	 * is fetched as soon as it arrives (one ahead of what is playing), so the first
	 * sentence plays while the model works on the rest.
	 */
	openStream(options: KonvoSpeakOptions & {onStart?: () => void; onPiece?: (text: string) => void}): KonvoSpeechStream {
		this.stop();
		const generation = this.generation;
		const voice = options.voiceId.replace(/^konvo:/, '');
		const chunks: Array<string> = [];
		const fetches = new Map<number, Promise<Blob>>();
		let index = 0;
		let playing = false;
		let ended = false;
		const fetchChunk = (at: number) => {
			let pending = fetches.get(at);
			if (!pending) {
				pending = http
					.post<Blob>(Endpoints.KONVO_ASSISTANT_SPEECH, {
						body: {text: chunks[at], voice, speed: options.rate},
						parse: 'binary',
					})
					.then((response) => {
						if (!(response.body instanceof Blob)) throw new Error('No audio');
						return response.body;
					});
				pending.catch(() => undefined);
				fetches.set(at, pending);
			}
			return pending;
		};
		const fail = (at: number, error: unknown) => {
			if (generation !== this.generation) return;
			this.stop();
			const unavailable = error instanceof HttpError && (error.status === 503 || error.status === 404);
			options.onError(chunks.slice(at).join(' '), unavailable);
		};
		const pump = () => {
			if (generation !== this.generation || playing) return;
			if (index >= chunks.length) {
				if (ended) {
					this.stop();
					options.onEnd();
				}
				return;
			}
			playing = true;
			const at = index;
			const pending = fetchChunk(at);
			if (at + 1 < chunks.length) fetchChunk(at + 1);
			pending
				.then((blob) => {
					if (generation !== this.generation) return;
					const audio = this.element();
					const url = URL.createObjectURL(blob);
					this.urls.push(url);
					audio.onended = () => {
						playing = false;
						index = at + 1;
						pump();
					};
					audio.onerror = () => fail(at, new Error('Playback failed'));
					audio.src = url;
					if (at === 0) options.onStart?.();
					options.onPiece?.(chunks[at]);
					return audio.play();
				})
				.catch((error) => fail(at, error));
		};
		const add = (pieces: Array<string>) => {
			if (generation !== this.generation || ended) return;
			chunks.push(...pieces.filter((piece) => piece.trim()));
			// Fetch the next piece while the current one plays.
			if (playing && index + 1 < chunks.length) fetchChunk(index + 1);
			pump();
		};
		return {
			push: (text) => add(streamPieces(text, chunks.length === 0)),
			pushPieces: add,
			end: () => {
				if (generation !== this.generation) return;
				ended = true;
				pump();
			},
		};
	}

	pause(): void {
		this.audio?.pause();
	}

	resume(): void {
		if (this.audio?.src) void this.audio.play().catch(() => undefined);
	}

	stop(): void {
		this.generation += 1;
		if (this.audio) {
			this.audio.onended = null;
			this.audio.onerror = null;
			this.audio.pause();
			this.audio.removeAttribute('src');
			this.audio.load();
		}
		for (const url of this.urls) URL.revokeObjectURL(url);
		this.urls = [];
	}
}

export default new KonvoVoicePlayer();
